import { OFXFile, OFXStatement, OFXTransaction, OFXAccount, BankCode, BRAZILIAN_BANKS } from '../../types/bank-reconciliation';

export class OFXParser {
  private static readonly OFX_DATE_FORMATS = [
    'YYYYMMDD',
    'YYYYMMDDHHMMSS',
    'YYYYMMDDHHMM',
    'YYYYMMDDHH'
  ];

  static async parseFile(file: File): Promise<OFXFile> {
    const content = await file.text();
    return this.parseContent(content);
  }

  static parseContent(content: string): OFXFile {
    try {
      // Normalize line endings and remove BOM
      const normalizedContent = content
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/^\uFEFF/, '');

      // Simple parsing for Brazilian OFX files
      return this.parseBrazilianOFX(normalizedContent);
    } catch (error) {
      throw new Error(`Erro ao processar arquivo OFX: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  }

  private static parseBrazilianOFX(content: string): OFXFile {
    const ofxFile: OFXFile = {
      statements: []
    };

    // Extract bank information from FI section if present
    const fiMatch = content.match(/<FI>\s*<ORG>([^<]+)<\/ORG>\s*<FID>([^<]+)<\/FID>/s);
    if (fiMatch) {
      ofxFile.org = fiMatch[1].trim();
      ofxFile.fid = fiMatch[2].trim();
    }

    // Find STMTRS section
    const stmtrsMatch = content.match(/<STMTRS>([\s\S]*?)<\/STMTRS>/);
    if (stmtrsMatch) {
      const statement = this.parseSTMTRS(stmtrsMatch[1]);
      if (statement) {
        ofxFile.statements.push(statement);
      }
    }

    // Try to determine bank code from FID or ORG
    if (ofxFile.fid || ofxFile.org) {
      ofxFile.bankId = this.determineBankCode(ofxFile.fid, ofxFile.org);
    }

    return ofxFile;
  }

  private static parseSTMTRS(stmtrsContent: string): OFXStatement | null {
    try {
      // Extract account information
      const bankAcctFromMatch = stmtrsContent.match(/<BANKACCTFROM>([\s\S]*?)<\/BANKACCTFROM>/);
      if (!bankAcctFromMatch) {
        throw new Error('Informações da conta não encontradas');
      }

      const bankAcctFrom = bankAcctFromMatch[1];
      const account: OFXAccount = {
        bankId: this.extractTag(bankAcctFrom, 'BANKID') || '',
        accountId: this.extractTag(bankAcctFrom, 'ACCTID') || '',
        accountType: this.extractTag(bankAcctFrom, 'ACCTTYPE') || 'CHECKING'
      };

      // Extract transactions
      const transactions: OFXTransaction[] = [];
      const banktranlistMatch = stmtrsContent.match(/<BANKTRANLIST>([\s\S]*?)<\/BANKTRANLIST>/);
      if (banktranlistMatch) {
        const banktranlist = banktranlistMatch[1];

  
        // Find all STMTTRN entries
        const stmttrnMatches = banktranlist.match(/<STMTTRN>([\s\S]*?)<\/STMTTRN>/g) || [];

        for (const stmttrn of stmttrnMatches) {
          const transaction = this.parseSTMTTRN(stmttrn);
          if (transaction) {
            transactions.push(transaction);
          }
        }
      }

      // Extract balance information
      const ledgerbalMatch = stmtrsContent.match(/<LEDGERBAL>([\s\S]*?)<\/LEDGERBAL>/);
      if (!ledgerbalMatch) {
        throw new Error('Informações de saldo não encontradas');
      }

      const ledgerbal = ledgerbalMatch[1];
      const balance = {
        amount: parseFloat(this.extractTag(ledgerbal, 'BALAMT') || '0'),
        dateAsOf: this.parseOFXDate(this.extractTag(ledgerbal, 'DTASOF') || '')
      };

      // Extract available balance if present
      let availableBalance;
      const availbalMatch = stmtrsContent.match(/<AVAILBAL>([\s\S]*?)<\/AVAILBAL>/);
      if (availbalMatch) {
        const availbal = availbalMatch[1];
        availableBalance = {
          amount: parseFloat(this.extractTag(availbal, 'BALAMT') || '0'),
          dateAsOf: this.parseOFXDate(this.extractTag(availbal, 'DTASOF') || '')
        };
      }

      // Extract date range
      const banktranlistDateMatch = stmtrsContent.match(/<BANKTRANLIST>([\s\S]*?)<\/BANKTRANLIST>/);
      let dateStart, dateEnd;
      if (banktranlistDateMatch) {
        const banktranlist = banktranlistDateMatch[1];
        dateStart = this.parseOFXDate(this.extractTag(banktranlist, 'DTSTART') || '');
        dateEnd = this.parseOFXDate(this.extractTag(banktranlist, 'DTEND') || '');
      }

      return {
        account,
        transactions,
        balance,
        availableBalance,
        dateStart,
        dateEnd
      };
    } catch (error) {
      console.error('Erro ao processar statement:', error);
      return null;
    }
  }

  private static parseSTMTTRN(stmttrnContent: string): OFXTransaction | null {
    try {
      const transaction: OFXTransaction = {
        type: this.extractTag(stmttrnContent, 'TRNTYPE') || 'OTHER',
        datePosted: this.parseOFXDate(this.extractTag(stmttrnContent, 'DTPOSTED') || ''),
        amount: parseFloat(this.extractTag(stmttrnContent, 'TRNAMT') || '0'),
        fitId: this.extractTag(stmttrnContent, 'FITID') || '',
        name: this.extractTag(stmttrnContent, 'NAME'),
        memo: this.extractTag(stmttrnContent, 'MEMO'),
        checkNum: this.extractTag(stmttrnContent, 'CHECKNUM'),
        refNum: this.extractTag(stmttrnContent, 'REFNUM')
      };

      // Validate critical fields
      if (!transaction.datePosted || isNaN(transaction.datePosted.getTime())) {
        console.warn('Data inválida na transação:', this.extractTag(stmttrnContent, 'DTPOSTED'));
      }

      // Parse optional dates
      const dtuser = this.extractTag(stmttrnContent, 'DTUSER');
      if (dtuser) {
        transaction.dateUser = this.parseOFXDate(dtuser);
      }

      const dtavail = this.extractTag(stmttrnContent, 'DTAVAIL');
      if (dtavail) {
        transaction.dateAvail = this.parseOFXDate(dtavail);
      }

  
      return transaction;
    } catch (error) {
      console.error('Erro ao processar transação:', error);
      return null;
    }
  }

  private static extractTag(content: string, tagName: string): string {
    // Remove todas as quebras de linha e espaços extras para processamento
    const normalizedContent = content.replace(/\s+/g, ' ').trim();

    // Procura pela tag mais flexível possível - OFX pode ter diferentes formatos
    const patterns = [
      // Padrão 1: Tags na mesma linha
      new RegExp(`<${tagName}[^>]*>([^<]*)<\/${tagName}>`, 'i'),
      // Padrão 2: Tags com conteúdo que pode incluir < (mas não </TAG>)
      new RegExp(`<${tagName}[^>]*>([\s\S]*?)<\/${tagName}>`, 'i'),
      // Padrão 3: Tags auto-fechantestype (SEM tag de fechamento - comum em OFX)
      new RegExp(`<${tagName}[^>]*>([^<]*)`, 'i'),
      // Padrão 4: Tag seguida por quebra de linha
      new RegExp(`<${tagName}[^>]*>\s*([^\s<][^<]*)`, 'i'),
      // Padrão 5: Mais permissivo - pega tudo após a tag até próxima tag
      new RegExp(`<${tagName}[^>]*>(.*?)(?=<[^/]|$)`, 'is')
    ];

    for (let i = 0; i < patterns.length; i++) {
      const match = normalizedContent.match(patterns[i]);

      if (match && match[1]) {
        let result = match[1].trim();

        // Limpeza adicional de caracteres especiais OFX
        result = result.replace(/^\s+|\s+$/g, ''); // Remove espaços nas extremidades
        result = result.replace(/\s+/g, ' '); // Normaliza espaços internos
        result = result.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ''); // Remove caracteres de controle

        // Verifica se o resultado é válido (não vazio após limpeza)
        if (result && result.length > 0) {
          return result;
        }
      }
    }

    // Se nenhum padrão funcionar, tenta uma abordagem mais agressiva
    const openingTag = normalizedContent.indexOf(`<${tagName}`);
    if (openingTag !== -1) {
      const afterOpeningTag = normalizedContent.substring(openingTag + `<${tagName}`.length);

      // Pula o possível fechamento da tag de abertura (>)
      const closeBracketIndex = afterOpeningTag.indexOf('>');
      if (closeBracketIndex !== -1) {
        const contentStart = afterOpeningTag.substring(closeBracketIndex + 1);

        // Tenta encontrar o início da próxima tag ou o fim do conteúdo
        const nextTagIndex = contentStart.indexOf('<');
        const result = nextTagIndex !== -1
          ? contentStart.substring(0, nextTagIndex).trim()
          : contentStart.trim();

        if (result && result.length > 0) {
          return result;
        }
      }
    }

    return '';
  }

  private static parseOFXDate(dateStr: string): Date {
    if (!dateStr) return new Date();

    // Remove timezone info if present (e.g., [-03:EST])
    const cleanDateStr = dateStr.replace(/\[-+][A-Z0-9:]+$/, '');

    // Handle different OFX date formats
    try {
      // Format: YYYYMMDDHHMMSS (length 14) - Most common OFX format
      if (cleanDateStr.length >= 14) {
        const year = parseInt(cleanDateStr.substring(0, 4));
        const month = parseInt(cleanDateStr.substring(4, 6)) - 1;
        const day = parseInt(cleanDateStr.substring(6, 8));
        const hour = parseInt(cleanDateStr.substring(8, 10));
        const minute = parseInt(cleanDateStr.substring(10, 12));
        const second = parseInt(cleanDateStr.substring(12, 14));
        return new Date(year, month, day, hour, minute, second);
      }

      // Format: YYYYMMDDHHMM (length 12)
      if (cleanDateStr.length === 12) {
        const year = parseInt(cleanDateStr.substring(0, 4));
        const month = parseInt(cleanDateStr.substring(4, 6)) - 1;
        const day = parseInt(cleanDateStr.substring(6, 8));
        const hour = parseInt(cleanDateStr.substring(8, 10));
        const minute = parseInt(cleanDateStr.substring(10, 12));
        return new Date(year, month, day, hour, minute, 0);
      }

      // Format: YYYYMMDD (length 8)
      if (cleanDateStr.length === 8) {
        const year = parseInt(cleanDateStr.substring(0, 4));
        const month = parseInt(cleanDateStr.substring(4, 6)) - 1;
        const day = parseInt(cleanDateStr.substring(6, 8));
        return new Date(year, month, day);
      }

      // Try to parse as ISO date format
      return new Date(cleanDateStr);
    } catch (error) {
      console.warn('Erro ao parsear data:', dateStr, error);
      // Return a valid date instead of invalid one
      return new Date();
    }
  }

  private static determineBankCode(fid?: string, org?: string): BankCode | undefined {
    // Bank code mappings
    const bankMappings: { [key: string]: BankCode } = {
      // FID mappings
      '001': '001', // Banco do Brasil
      '033': '033', // Santander
      '0341': '341', // Itaú
      '104': '104', // Caixa
      '237': '237', // Bradesco
      '260': '260', // Nu Pagamentos
      '077': '077', // Banco Inter
      '208': '208', // BTG Pactual
      '756': '756', // Bancoob
      '748': '748', // Sicredi
      '045': '045', // HSBC
      '707': '707', // Daycoval
      '623': '623', // Panamericano
      '212': '212', // Original
      '422': '422', // Safra
      '90400888': '033', // Santander
      '00360305': '104', // Caixa

      // ORG mappings
      'Banco do Brasil': '001',
      'Banco Santander': '033',
      'CAIXA': '104',
      'Bradesco': '237',
      'Nu Pagamentos S.A.': '260',
      'Banco Itau': '341',
      'Banco Inter': '077',
      'Banco BTG Pactual S.A.': '208',
      'BANCOOB': '756',
      'Sicredi': '748'
    };

    if (fid && bankMappings[fid]) {
      return bankMappings[fid];
    }

    if (org && bankMappings[org]) {
      return bankMappings[org];
    }

    // Try partial matches for organization names
    if (org) {
      const orgLower = org.toLowerCase();
      for (const [key, value] of Object.entries(bankMappings)) {
        if (key.toLowerCase().includes(orgLower) || orgLower.includes(key.toLowerCase())) {
          return value;
        }
      }
    }

    return undefined;
  }

  static validateOFXFile(file: File): { isValid: boolean; error?: string } {
    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith('.ofx')) {
      return { isValid: false, error: 'O arquivo deve ter extensão .ofx' };
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB
      return { isValid: false, error: 'O arquivo não pode ser maior que 50MB' };
    }

    return { isValid: true };
  }
}